interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

// Extract the up-to-10 national digits (7XX XXX XX XX) from any input.
// Our own formatted value always carries a literal "+7" prefix, whose "7" must
// NOT be counted as a national digit — so when a "+" is present we drop the
// leading 7 (country code). Raw pastes are handled too (8…/7…XXXXXXXXXX).
function toNationalDigits(input: string): string {
  let d = (input || "").replace(/\D/g, "");
  const hasPlus = (input || "").includes("+");
  if (hasPlus && d[0] === "7") d = d.slice(1); // our "+7" country prefix
  else if (!hasPlus && d[0] === "8") d = d.slice(1); // trunk prefix
  else if (!hasPlus && d.length === 11 && d[0] === "7") d = d.slice(1); // pasted 7XXXXXXXXXX
  return d.slice(0, 10);
}

function maskFromDigits(d: string): string {
  if (d.length === 0) return "";
  let out = "+7 (" + d.slice(0, 3);
  if (d.length >= 3) out += ")";
  if (d.length > 3) out += " " + d.slice(3, 6);
  if (d.length > 6) out += "-" + d.slice(6, 8);
  if (d.length > 8) out += "-" + d.slice(8, 10);
  return out;
}

export default function PhoneInput({
  value,
  onChange,
  placeholder = "+7 (___) ___-__-__",
}: PhoneInputProps) {
  function handle(e: React.ChangeEvent<HTMLInputElement>) {
    let raw = e.target.value;
    // If the keystroke deleted a formatting char (separator), also drop the
    // digit before it — so each Backspace removes exactly one digit.
    if (raw.length < value.length && /\D/.test(value[value.length - 1] ?? "")) {
      raw = raw.replace(/\d(?=\D*$)/, "");
    }
    onChange(maskFromDigits(toNationalDigits(raw)));
  }

  return (
    <input
      type="tel"
      inputMode="tel"
      autoComplete="tel"
      maxLength={18} // "+7 (705) 123-45-67"
      value={value}
      onChange={handle}
      placeholder={placeholder}
      className="w-full bg-input text-black rounded-[14px] px-4 py-3 text-base placeholder:text-grey/70 outline-none focus:ring-2 focus:ring-violet"
    />
  );
}
